import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Shared submit form for non-scheduled inquiries (consultation, group-class, virtual-consulting).
 * The page passes a `fields` array describing extra inputs to render after the shared
 * name/email/phone block. Each item: { name, label, type?, hint?, options?, required? }.
 */
export default function InquiryForm({
  requestType,
  fields = [],
  submitLabel = "Send request",
  testidPrefix = "inquiry",
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    ...Object.fromEntries(fields.map((f) => [f.name, f.type === "checkbox-list" ? [] : ""])),
  });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const toggleAddon = (val) =>
    setForm((f) => {
      const cur = new Set(f.consultation_addons || []);
      cur.has(val) ? cur.delete(val) : cur.add(val);
      return { ...f, consultation_addons: Array.from(cur) };
    });

  const requiredFields = ["name", "email", ...fields.filter((f) => f.required).map((f) => f.name)];
  const valid =
    requiredFields.every((k) => {
      const v = form[k];
      return Array.isArray(v) ? v.length > 0 : !!String(v || "").trim();
    });

  const onSubmit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const payload = { request_type: requestType, ...form, origin_url: window.location.origin };
      // Coerce numeric fields
      if (payload.group_size) payload.group_size = parseInt(payload.group_size, 10) || null;
      if (payload.consultation_hours) payload.consultation_hours = parseInt(payload.consultation_hours, 10) || null;
      const { data } = await api.post("/service-requests", payload);
      navigate(`/request-thanks/${data.id}?type=${requestType}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8 space-y-6">
      {/* Shared contact block */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Your name" required>
          <Input value={form.name} onChange={set("name")} autoComplete="off" name={`${testidPrefix}_name`} data-testid={`${testidPrefix}-name`} />
        </Field>
        <Field label="Email" required>
          <Input type="email" value={form.email} onChange={set("email")} autoComplete="off" name={`${testidPrefix}_email`} data-testid={`${testidPrefix}-email`} />
        </Field>
        <Field label="Phone (optional)">
          <Input value={form.phone} onChange={set("phone")} autoComplete="off" name={`${testidPrefix}_phone`} data-testid={`${testidPrefix}-phone`} />
        </Field>
      </div>

      {/* Type-specific fields */}
      {fields.map((f) => {
        if (f.type === "select") {
          return (
            <Field key={f.name} label={f.label} hint={f.hint} required={f.required}>
              <select
                value={form[f.name]}
                onChange={set(f.name)}
                className="w-full rounded-md border border-gg-border bg-gg-bg px-3 py-2 text-sm focus:border-gg-green focus:outline-none"
                data-testid={`${testidPrefix}-${f.name}`}
                name={`${testidPrefix}_${f.name}`}
              >
                <option value="">— choose —</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          );
        }
        if (f.type === "checkbox-list") {
          return (
            <Field key={f.name} label={f.label} hint={f.hint}>
              <div className="space-y-2">
                {f.options.map((o) => {
                  const checked = (form[f.name] || []).includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggleAddon(o.value)}
                      className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                        checked
                          ? "border-gg-green bg-gg-green/8 ring-1 ring-gg-green/30"
                          : "border-gg-border bg-gg-bg hover:border-gg-green/50"
                      }`}
                      data-testid={`${testidPrefix}-${f.name}-${o.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{o.label}</p>
                        {o.price && <span className="text-sm text-gg-brown font-mono">{o.price}</span>}
                      </div>
                      {o.note && <p className="text-xs text-gg-muted mt-1">{o.note}</p>}
                    </button>
                  );
                })}
              </div>
            </Field>
          );
        }
        return (
          <Field key={f.name} label={f.label} hint={f.hint} required={f.required}>
            <Input
              type={f.type || "text"}
              value={form[f.name]}
              onChange={set(f.name)}
              placeholder={f.placeholder || ""}
              autoComplete="off"
              name={`${testidPrefix}_${f.name}`}
              data-testid={`${testidPrefix}-${f.name}`}
            />
          </Field>
        );
      })}

      <Field label="Notes / details (optional)">
        <Textarea
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder="Anything else Heather should know?"
          data-testid={`${testidPrefix}-message`}
        />
      </Field>

      <button
        type="button"
        onClick={onSubmit}
        disabled={!valid || submitting}
        className="btn-pill btn-primary w-full text-base disabled:opacity-50"
        data-testid={`${testidPrefix}-submit`}
      >
        {submitting ? "Sending…" : submitLabel} <ArrowRight size={16} />
      </button>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <Label className="block space-y-1.5">
      <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
        {label}{required && <span className="text-[#B24C4C] ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-gg-muted">{hint}</span>}
    </Label>
  );
}
